import { Button, Form, Input, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProductCategories } from "./useProductCategories";
import { ProductVarieties } from "./ProductVarieties";

interface IProps {
  value?: {
    parent?: {
      id?: number;
      childId?: number | null;
    } | null;
    varieties?: Array<{
      nameRu: string;
      nameKy: string;
    }>;
  };
  onChange?: (data: any) => void;
  onValidityChange?: (isValid: boolean) => void;
}

interface SubcategoryForm {
  nameRu: string;
  nameKy: string;
  isEditing?: boolean;
}

const SelectProduct = ({ value, onChange, onValidityChange }: IProps) => {
  const { t } = useTranslation();
  const [currentForm, setCurrentForm] = useState<SubcategoryForm>({
    nameRu: "",
    nameKy: "",
  });
  const [touched, setTouched] = useState({
    category: false,
    child: false
  });
  const [savedForms, setSavedForms] = useState<SubcategoryForm[]>(
    value?.varieties?.map((item) => ({ ...item, isEditing: false })) || []
  );

  const {
    selectedCategory,
    setSelectedCategory,
    selectedChild,
    setSelectedChild,
    hasChildren,
    getCategoryOptions,
    getChildOptions,
    categories
  } = useProductCategories({
    initialCategoryId: value?.parent?.id || null,
    initialChildId: value?.parent?.childId || null
  });

  useEffect(() => {
    if (value?.varieties) {
      console.log('SelectProduct received value:', value);
      setSavedForms(value.varieties.map((item) => ({ ...item, isEditing: false })));
    }
  }, [value?.varieties]);

  useEffect(() => {
    const selectedCat = categories.find(c => c.id === selectedCategory);
    console.log('Selected category:', selectedCat);
    console.log('Selected child:', selectedChild);
    const hasChildCategories = selectedCat?.children && selectedCat.children.length > 0;
    
    const isValid = selectedCategory !== null && 
      (!hasChildCategories || (hasChildCategories && selectedChild !== null));
    
    onValidityChange?.(isValid);
  }, [selectedCategory, selectedChild, categories]);

  const notifyChange = (forms: SubcategoryForm[], categoryId: number | null, childId: number | null) => {
    if (!categoryId) {
      return;
    }

    const cleaned = forms.map(({ isEditing, ...rest }) => rest);
    
    const selectedCat = categories.find(c => c.id === categoryId);
    const hasChildCategories = selectedCat?.children && selectedCat.children.length > 0;

    if (hasChildCategories && !childId) {
      return;
    }

    const data = {
      parent: { 
        id: categoryId,
        childId: childId
      },
      varieties: cleaned,
      categoryId: categoryId
    };
    
    onChange?.(data);
  };

  const handleCategoryChange = (val: number | null) => {
    setTouched(prev => ({ ...prev, category: true }));
    setSelectedCategory(val);
    setSelectedChild(null);
    setTouched(prev => ({ ...prev, child: false }));
    notifyChange(savedForms, val, null);
  };

  const handleChildChange = (val: number | null) => {
    setTouched(prev => ({ ...prev, child: true }));
    setSelectedChild(val);
    notifyChange(savedForms, selectedCategory, val);
  };

  const shouldShowError = (field: 'category' | 'child') => {
    if (!touched[field]) return false;
    
    if (field === 'category') {
      return !selectedCategory;
    }
    
    if (field === 'child') {
      const selectedCat = categories.find(c => c.id === selectedCategory);
      const hasChildCategories = selectedCat?.children && selectedCat.children.length > 0;
      return hasChildCategories && !selectedChild;
    }
    
    return false;
  };

  const handleCurrentFormChange = (field: keyof SubcategoryForm, value: string) => {
    setCurrentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSavedFormChange = (
    index: number,
    field: keyof SubcategoryForm,
    value: string
  ) => {
    setSavedForms((prev) =>
      prev.map((form, i) =>
        i === index ? { ...form, [field]: value } : form
      )
    );
  };

  const addSubcategory = () => {
    if (currentForm.nameRu || currentForm.nameKy) {
      setSavedForms((prev) => [...prev, { ...currentForm, isEditing: false }]);
      setCurrentForm({ nameRu: "", nameKy: "" });
      notifyChange(
        [...savedForms, { ...currentForm, isEditing: false }],
        selectedCategory,
        selectedChild
      );
    }
  };

  const removeSubcategory = (index: number) => {
    setSavedForms((prev) => {
      const newForms = prev.filter((_, i) => i !== index);
      notifyChange(newForms, selectedCategory, selectedChild);
      return newForms;
    });
  };

  const toggleEdit = (index: number) => {
    setSavedForms((prev) =>
      prev.map((form, i) =>
        i === index ? { ...form, isEditing: true } : form
      )
    );
  };

  const saveChanges = (index: number) => {
    setSavedForms((prev) => {
      const newForms = prev.map((form, i) =>
        i === index ? { ...form, isEditing: false } : form
      );
      notifyChange(newForms, selectedCategory, selectedChild);
      return newForms;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form.Item 
          label={ t('handbooks.types_of_products.Product Category') }
          style={{ marginBottom: 0 }}
          validateStatus={shouldShowError('category') ? 'error' : undefined}
          help={shouldShowError('category') ? 'Необходимо выбрать категорию' : undefined}
        >
          <Select
            allowClear
            value={selectedCategory}
            onChange={handleCategoryChange}
            style={{ width: '100%' }}
            options={getCategoryOptions()}
            optionFilterProp="label"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            status={shouldShowError('category') ? 'error' : undefined}
          />
        </Form.Item>

        {selectedCategory && hasChildren() && (
          <Form.Item 
            label={ t('handbooks.types_of_products.Product Subcategory') } 
            style={{ marginBottom: 0 }}
            validateStatus={shouldShowError('child') ? 'error' : undefined}
            help={shouldShowError('child') ? 'Необходимо выбрать подкатегорию' : undefined}
          >
            <Select
              allowClear
              value={selectedChild}
              onChange={handleChildChange}
              style={{ width: '100%' }}
              options={getChildOptions()}
              optionFilterProp="label"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              status={shouldShowError('child') ? 'error' : undefined}
            />
          </Form.Item>
        )}
      </Space>

      <ProductVarieties
        currentForm={currentForm}
        savedForms={savedForms}
        onCurrentFormChange={handleCurrentFormChange}
        onSavedFormChange={handleSavedFormChange}
        onAddSubcategory={addSubcategory}
        onRemoveSubcategory={removeSubcategory}
        onToggleEdit={toggleEdit}
        onSaveChanges={saveChanges}
      />
    </div>
  );
};

export default SelectProduct;
